import axios from 'axios';

const LANGFLOW_URL = process.env.REACT_APP_LANGFLOW_URL || 'http://localhost:7860';
const FLOW_ID = process.env.REACT_APP_LANGFLOW_FLOW_ID || '';
const API_KEY = process.env.REACT_APP_LANGFLOW_API_KEY || '';

function getHeaders() {
    const headers = { 'Content-Type': 'application/json' };
    if (API_KEY) {
        headers['x-api-key'] = API_KEY;
    }
    return headers;
}

function extractText(data) {
    const outputs = data?.outputs;
    if (outputs && outputs[0]?.outputs?.[0]?.results?.message?.text) {
        return outputs[0].outputs[0].results.message.text;
    }
    return null;
}

function delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Fetch ALL results from Docker — mirrors search_dashboard.py.
 * Reads essentials.data (with title, assignee, pub_date) + titles from .seq files.
 * Single API call, no pagination.
 */
async function fetchAllResults(workflowId, userId) {
    return { totalRows: 0, uniqueEntries: 0, totalSequences: 0, results: [] };
    /*
    console.log(`[AiData] Fetching workflow data from Docker: workflowId=${workflowId}, userId=${userId}`);

    try {
        const resp = await axios.get('/api/docker-workflow', {
            params: { workflowId, userId },
        });

        const data = resp.data;
        if (!data || !data.results || data.results.length === 0) {
            console.warn('[AiData] No results from Docker');
            return { totalRows: 0, uniqueEntries: 0, totalSequences: 0, results: [] };
        }

        console.log(`[AiData] Docker load complete: ${data.unique_entries} unique patents, ${data.total_results} total rows, ${data.total_sequences} sequences`);
        if (data.results.length > 0) {
            console.log('[AiData] Sample:', JSON.stringify(data.results[0]));
        }

        return {
            workflow_id: data.workflow_id,
            totalRows: data.total_results,
            uniqueEntries: data.unique_entries,
            totalSequences: data.total_sequences,
            aggregate_stats: data.aggregate_stats || {},
            results: data.results,
        };
    } catch (err) {
        console.error('[AiData] Docker fetch failed:', err.message);
        return { totalRows: 0, uniqueEntries: 0, totalSequences: 0, results: [] };
    }
    */
}

/**
 * Build compact data for the AIS flow.
 * Sends a structured payload with 5 feature sections:
 *   1. Claim vs Disclosure analysis (FTO risk)
 *   2. Cross-patent summary data (titles, assignees, claims)
 *   3. Alignment interpretation (per-hit stats: identity, coverage, gaps, positions)
 *   4. Natural-language filtering (pre-indexed prefix/assignee/database counts)
 *   5. Brief/report generation (aggregate statistics, legal status, jurisdictions)
 */
function buildCompactData(patentData, resInfo, question) {
    const results = patentData?.results || [];
    const aggregateStats = patentData?.aggregate_stats || {};

    // Pre-compute counts (for natural-language filtering)
    const prefixCounts = {};
    const assigneeCounts = {};
    const databaseCounts = {};
    for (const r of results) {
        const pn = r.patent_number || '';
        let prefix = '';
        for (const ch of pn) {
            if (/[A-Za-z]/.test(ch)) prefix += ch.toUpperCase();
            else break;
        }
        if (prefix) prefixCounts[prefix] = (prefixCounts[prefix] || 0) + 1;
        const aKey = r.assignee || 'Unknown';
        assigneeCounts[aKey] = (assigneeCounts[aKey] || 0) + 1;
        const dKey = r.database || 'Unknown';
        databaseCounts[dKey] = (databaseCounts[dKey] || 0) + 1;
    }

    // Extract patent numbers mentioned in the question
    const mentionedPatents = [];
    if (question) {
        const pnPattern = /[A-Z]{2}\d{5,}/gi;
        const mentionedPns = new Set([...(question.match(pnPattern) || [])].map(m => m.toUpperCase()));
        if (mentionedPns.size > 0) {
            for (const r of results) {
                const rpn = (r.patent_number || '').toUpperCase();
                if (rpn && mentionedPns.has(rpn)) mentionedPatents.push(r);
            }
        }
    }

    // ── Build top-level workflow metadata ──
    const compact = {
        workflow_id: resInfo?.title || patentData?.workflow_id || '',
        algorithm: resInfo?.algorithm || '',
        total_results: patentData?.totalRows || 0,
        unique_entries: patentData?.uniqueEntries || 0,
        total_sequences: patentData?.totalSequences || 0,

        // ── Feature #4: Natural-language filtering indexes ──
        summary: {
            patents_by_country_prefix: prefixCounts,
            patents_by_assignee: Object.fromEntries(
                Object.entries(assigneeCounts).sort((a, b) => b[1] - a[1]).slice(0, 50)
            ),
            patents_by_database: databaseCounts,
            total_patents_in_results: results.length,
        },

        // ── Feature #5: Brief / report generation ──
        aggregate_statistics: {
            total_hits: aggregateStats.total_hits || 0,
            total_queries: aggregateStats.total_queries || 0,
            queries_with_hits: aggregateStats.queries_with_hits || 0,
            queries_no_hits: aggregateStats.queries_no_hits || 0,
            hits_at_100pct_identity: aggregateStats.hits_100pct || 0,
            total_patents: aggregateStats.total_patents || 0,
            total_patent_families: aggregateStats.total_patent_families || 0,
            earliest_patent: aggregateStats.earliest_patent || '',
            legal_status: aggregateStats.legal_status || {},
            by_jurisdiction: aggregateStats.by_jurisdiction || {},
        },

        // ── Feature #1: Claim vs Disclosure / FTO risk summary ──
        fto_summary: aggregateStats.fto_summary || {
            high_risk_patents: 0,
            medium_risk_patents: 0,
            low_risk_patents: 0,
            total_claimed_sequences: 0,
            total_disclosed_sequences: 0,
        },

        results_note: [
            'Each result contains: basic info (title, assignee, date), FTO data (claimed/disclosed seq IDs, claim numbers), alignment stats (identity %, coverage, gaps, positions).',
            'IMPORTANT — Identity metrics (three different percentages, pick the right one):',
            '  • identity_alignment_pct (RI) = matches / alignment_length. THIS IS WHAT THE UI COLUMN "% Id. Alignment" SHOWS. Use this for "what is the identity percentage?" questions.',
            '  • identity_query_pct (RIQ) = matches / query length aligned portion. Use for "% Id. Query" questions.',
            '  • identity_subject_pct (RID) = matches / subject length. Usually tiny; rarely useful.',
            'Gap reporting: "has_gaps" boolean plus gaps_query and gaps_subject counts. "has_any_gaps" at patent level summarizes across all alignments.',
            'Use aggregate_statistics for counts, fto_summary for risk rollup.',
        ].join(' '),
        results: [],
    };

    // ── Per-patent entries with all 5 feature data ──
    const includedPns = new Set();
    const pushEntry = (r) => {
        compact.results.push({
            // Feature #2: Cross-patent summary data
            patent_number: r.patent_number,
            title: r.title || null,
            assignee: r.assignee || null,
            earliest_pub_date: r.earliest_pub_date,
            database: r.database,
            seq_count: r.seq_count,
            subject_seq_ids: r.subject_seq_ids,

            // Feature #1: Claim vs Disclosure (FTO)
            fto_risk_level: r.fto_risk_level || 'unknown',
            psl_status: r.psl_status || 'unknown',
            claimed_seq_ids: r.claimed_seq_ids || [],
            disclosed_seq_ids: r.disclosed_seq_ids || [],
            claim_details: r.claim_details || {},

            // Feature #3: Alignment interpretation
            alignments: r.alignments || [],
            best_identity_alignment_pct: r.best_identity_alignment_pct || 0,  // RI — matches the UI's "% Id. Alignment" column
            best_identity_query_pct: r.best_identity_query_pct || 0,          // RIQ — matches the UI's "% Id. Query" column
            max_alignment_length: r.max_alignment_length || 0,
            has_any_gaps: r.has_any_gaps || false,
        });
        if (r.patent_number) includedPns.add(r.patent_number.toUpperCase());
    };

    for (const r of results) pushEntry(r);

    // Inject mentioned patents not already included
    for (const r of mentionedPatents) {
        const rpn = (r.patent_number || '').toUpperCase();
        if (rpn && !includedPns.has(rpn)) {
            pushEntry(r);
            console.log(`[CompactData] Injected patent ${r.patent_number}`);
        }
    }

    return compact;
}

async function pollForResult(sessionId) {
    const headers = getHeaders();
    const url = `${LANGFLOW_URL}/api/v1/monitor/messages?session_id=${sessionId}`;

    for (let i = 0; i < 30; i++) {
        await delay(4000);
        try {
            const resp = await axios.get(url, { headers });
            if (resp.status !== 200) continue;

            const messages = resp.data;
            for (let j = messages.length - 1; j >= 0; j--) {
                const msg = messages[j];
                if (msg.sender === 'Machine' && msg.text && msg.text.trim()) {
                    return msg.text.trim();
                }
            }
        } catch (err) {
            console.warn(`[Langflow] Poll ${i + 1} error:`, err.message);
        }
    }

    throw new Error('AI response not received within 120 seconds. Please try again.');
}

async function sendMessage(userMessage, discoverData) {
    const headers = getHeaders();
    const { resInfo, allResults } = discoverData || {};

    const compactData = buildCompactData(allResults, resInfo, userMessage);
    compactData.user_question = userMessage;

    const payload = {
        input_value: userMessage,
        output_type: 'chat',
        input_type: 'chat',
        tweaks: {
            'CreateData-9zxmL': {
                json_data: JSON.stringify(compactData)
            }
        }
    };

    console.log(`[Langflow] Sending ALL ${compactData.results?.length || 0} unique patents, payload: ${JSON.stringify(payload).length} bytes`);

    const response = await axios.post(
        `${LANGFLOW_URL}/api/v1/run/${FLOW_ID}?stream=false`,
        payload,
        { headers }
    );

    const text = extractText(response.data);
    if (text) return text;

    const data = response.data;
    if (data.status === 'pending' && data.session_id) {
        return pollForResult(data.session_id);
    }

    return JSON.stringify(data);
}

/**
 * Download the exact JSON that would be sent to Langflow as a file.
 */
function downloadPayload(discoverData, question) {
    const { resInfo, allResults } = discoverData || {};
    const compactData = buildCompactData(allResults, resInfo, question || 'sample question');
    compactData.user_question = question || 'sample question';

    const payload = {
        input_value: question || 'sample question',
        output_type: 'chat',
        input_type: 'chat',
        tweaks: {
            'CreateData-9zxmL': {
                json_data: JSON.stringify(compactData)
            }
        }
    };

    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `langflow_payload_${Date.now()}.json`;
    link.click();
    URL.revokeObjectURL(url);

    console.log(`[Langflow] Downloaded payload: ${compactData.results?.length || 0} unique patents, ${JSON.stringify(payload).length} bytes`);
}

const langflowService = {
    sendMessage,
    fetchAllResults,
    downloadPayload,
};

export default langflowService;

